import { ReactElement, useState } from "react";
import { createPortal } from "react-dom";

import { Client } from "@/Common/Api/Client";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LinkButton } from "@/DesignSystem";
import { RiskFactorAutocomplete } from "@/Patient/Components/MedicalItems/RiskFactorAutoComplete";
import { RiskFactorEditList } from "@/Patient/Components/MedicalItems/RiskFactorEditList";
import { MedicalItemRiskFactorHelper } from "@/Patient/Helpers/MedicalItemRiskFactorHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { RiskFactorCategoryCode } from "@/Patient/Models/MedicalSummary/RiskFactorCode";

import styles from "./AddRiskFactorsPopup.module.scss";

interface AddRiskFactorsPopupProps {
    patientId: string;
    encounterDate: Date;
    onClose: () => void;
    onSave: () => void;
}

function AddRiskFactorsPopup(props: Readonly<AddRiskFactorsPopupProps>): ReactElement {
    const { getText } = useTranslation();

    const [selectedRiskFactorId, setSelectedRiskFactorId] = useState<string | null>(null);
    const [details, setDetails] = useState<string>("");
    const [dateTextValue, setDateTextValue] = useState<string>("");
    const [dateValue, setDateValue] = useState<Date | null>(null);

    function onChangeDate(dateWithPrecision: DateTimeWithPrecision): void {
        setDateTextValue(dateWithPrecision.text);

        if (dateWithPrecision.status !== LifeStageDateStatusEnum.Valid && !dateWithPrecision.isDayPrecision) {
            setDateValue(null);
        }
        else {
            setDateValue(dateWithPrecision.value);
        }
    }

    async function saveNewRiskFactor(): Promise<void> {
        const newMedicalItem = MedicalItem.newItem(null, MedicalItemTypeEnum.SocialHabitsAndRiskFactors) as MedicalItemRiskFactor;
        newMedicalItem.RiskFactorCodeId = selectedRiskFactorId;
        newMedicalItem.Name = MedicalItemRiskFactorHelper.getRiskFactorLabelById(selectedRiskFactorId);
        newMedicalItem.LifeStageMode = LifeStageModeEnum.Date;
        newMedicalItem.LifeStageDate = dateValue;
        newMedicalItem.ConsultationDate = props.encounterDate;
        newMedicalItem.Details = details;

        const patientModel = patientStore.PatientModels[props.patientId];
        const encounter = patientStore.PatientMedicalNoteModels[props.patientId].get("Encounter");
        newMedicalItem.RelatedEncounterId = encounter.Id;

        const requestData = {
            PatientContactId: props.patientId,
            PatientContactFacetId: patientModel.PatientResponse.ContactFacet_id,
            MedicalItems: [newMedicalItem],
            SaveInNewEncounter: false,
            LatestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees: null
        };

        const response = await Client.postAsync<{ MedicalItems: MedicalItemRiskFactor[] }>(
            ServerOperations.Server.Myle_Patient_MedicalItem_SaveMedicalItemRiskFactor,
            requestData
        );

        if (response.isSuccess) {
            sessionStore.dispatch(
                LocalPatientOperations.MedicalItems.SaveRiskFactors,
                {
                    riskFactors: response.data.MedicalItems
                }
            );
        }
    }

    return createPortal(
        <Modal
            title={getText("AddRiskFactorPopup_Title")}
            onClose={() => props.onClose()}
            showX
            width={750}
            footer={(
                <div className={styles.footer}>
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => props.onClose()}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            disabled={!selectedRiskFactorId || !dateValue}
                            onClick={async () => {
                                await saveNewRiskFactor();
                                props.onSave();
                            }}
                            label={getText("SaveLabel")}
                        />
                    </ButtonGroup>
                </div>
            )}
            showOverlay
            disableAutoScroll
        >
            <div className={styles.container}>
                <div className={styles.gridContainer}>
                    <label>
                        {getText("SocialHabitLabel")}
                    </label>
                    <div>
                        {selectedRiskFactorId
                            ? (
                                <RiskFactorEditList
                                    riskFactors={[{ RiskFactorCodeId: selectedRiskFactorId, Details: details, Name: MedicalItemRiskFactorHelper.getRiskFactorLabelById(selectedRiskFactorId) }]}
                                    onRemove={() => {
                                        setSelectedRiskFactorId(null);
                                        setDetails("");
                                    }}
                                    onChangeDetails={(_, details) => setDetails(details)}
                                />
                            ) : (
                                <RiskFactorAutocomplete
                                    onItemSelectCallback={item => {
                                        setSelectedRiskFactorId(item.Id);
                                        setTimeout(() => {
                                            const elements = document.getElementsByClassName("riskfactor-details-input");
                                            if (elements.length > 0) {
                                                (elements[0] as HTMLInputElement).focus?.();
                                            }
                                        }, 100);
                                    }}
                                    filter={[RiskFactorCategoryCode.Tobacco, RiskFactorCategoryCode.Alcohol]}
                                />
                            )
                        }
                    </div>
                    <div />
                    <p className={styles.warningMessage}>{getText("AddRiskFactorPopup_EntryWillBeAddedtoCppLabel")}</p>
                    <label>{getText("DateLabel")}</label>
                    <div>
                        <TextboxControlWithDatePicker
                            id="add-risk-factor-date"
                            expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                            isInvalid={!dateValue}
                            textValue={dateTextValue}
                            dateValue={dateValue}
                            onChange={onChangeDate}
                        />
                    </div>
                    <div />
                    <div>
                        <LinkButton
                            onClick={() => {
                                onChangeDate(new DateTimeWithPrecision(
                                    LuxonDateTimeParser.parse(props.encounterDate).toFormat("yyyy-MM-dd").toString(),
                                    dateTextValue,
                                    LifeStageDatePrecisionEnum.Day
                                ));
                            }}
                            underline={false}
                            label={getText("TodayLabel")}
                        />
                    </div>
                </div>
            </div>
        </Modal>,
        document.body
    );
}

export { AddRiskFactorsPopup };
